import { useEffect, useState } from "react";
import { getRolePermissions } from "../services/roleService";
import {
  CasbinPolicy,
  RolePermissionsByEntity,
  UseRolePermissionsResult,
} from "../types/permissions";
import { PERMISSION_ENTITIES } from "../config/permissionsConfig";

// Page size used when fetching role permissions.  Must exceed the total number
// of valid entity:action pairs in the current UI config (143 as of DQ-8615).
// Using 500 gives a comfortable margin for future entity additions.
export const PERMISSIONS_FETCH_PAGE_SIZE = 500;

// Build a set of all valid "entity:action" pairs from the UI config so that
// stale or renamed entities stored in the database (e.g. the old generic
// "run_scoring_config" that has since been replaced by more-specific entities)
// are silently dropped and never forwarded to the backend save request.
const VALID_ENTITY_ACTIONS = new Set<string>(
  PERMISSION_ENTITIES.flatMap((ent) =>
    ent.actions.map((a) => `${ent.key}:${a.key}`)
  )
);

interface UseFetchRolePermissionsProps {
  roleId: number | null;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const useFetchRolePermissions = ({
  roleId,
  setIsLoading,
}: UseFetchRolePermissionsProps): UseRolePermissionsResult => {
  const [permissions, setPermissions] = useState<RolePermissionsByEntity>({});
  const [isLoading, setInternalLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    if (!roleId) {
      setPermissions({});
      return;
    }

    const loadingState = setIsLoading || setInternalLoading;
    loadingState(true);
    setError(null);

    try {
      const response = await getRolePermissions({
        role_id: roleId,
        is_active: true,
        page: 1,
        // Use a page size well above the maximum possible permissions (145 total
        // entity:action pairs in the current UI config) so that all policies are
        // always returned in a single request.  The previous value of 100 was
        // insufficient: a role with all view + add + edit permissions active has
        // more than 100 policies, causing some to be silently dropped, which in
        // turn deleted them from the database on the next save (DQ-8615).
        size: PERMISSIONS_FETCH_PAGE_SIZE,
      });

      // Convert the flat list of policies to grouped permissions.
      // Filter out any entity:action pair not recognised by the current UI
      // config — this prevents legacy/renamed entities (e.g. "run_scoring_config")
      // that are still stored in the database from being re-submitted to the
      // backend on save, which would cause a 422 validation error.
      const groupedPermissions: RolePermissionsByEntity = {};

      response.items.forEach((policy: CasbinPolicy) => {
        if (!VALID_ENTITY_ACTIONS.has(`${policy.entity}:${policy.action}`)) {
          return; // skip stale / unrecognised entity-action pairs
        }
        if (!groupedPermissions[policy.entity]) {
          groupedPermissions[policy.entity] = {};
        }
        groupedPermissions[policy.entity][policy.action] = policy.is_active;
      });

      setPermissions(groupedPermissions);
    } catch (err: any) {
      console.error("Error fetching role permissions:", err);
      setError(err?.message || "Failed to fetch role permissions");
      setPermissions({});
    } finally {
      loadingState(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [roleId]);

  return {
    permissions,
    isLoading: setIsLoading ? false : isLoading,
    error,
    refetch: fetchPermissions,
  };
};

export default useFetchRolePermissions;
